import { FiActivity } from 'react-icons/fi';

export const gerundsData = {
  courseId: 'gerunds',
  courseType: 'grammar',
  title: 'Gerunds',
  icon: FiActivity,
  overview: {
    impact: 'Essential for natural-sounding English',
    focus: 'Using -ing forms as nouns',
    uses: 'Writing and Speaking',
    duration: '20 minutes',
  },
  sections: [
    {
      title: 'What Are Gerunds?',
      content: `<p>A gerund is a verb form ending in <strong>-ing</strong> that functions as a noun. Despite looking like a verb, it acts as the subject, object, or complement in a sentence.</p>`,
      examples: [
        { label: 'Subject', text: 'Swimming is great exercise.' },
        { label: 'Object', text: 'I enjoy reading.' },
        { label: 'Complement', text: 'My hobby is painting.' },
      ],
    },
    {
      title: 'Verbs Followed by Gerunds',
      content: `<p>Certain verbs must be followed by a gerund, not an infinitive:</p>`,
      points: [
        'enjoy: "I enjoy learning new languages."',
        'avoid: "She avoids making mistakes."',
        'consider: "They considered moving abroad."',
        'suggest: "He suggested taking a break."',
        'finish: "We finished writing the report."',
        'keep: "Keep practicing every day."',
        'mind: "Do you mind waiting?"',
        'practice: "Practice speaking English daily."',
      ],
    },
    {
      title: 'Gerunds After Prepositions',
      content: `<p>After prepositions, always use a gerund (never an infinitive):</p>`,
      examples: [
        { label: 'interested in', text: 'I\'m interested in learning about history.' },
        { label: 'good at', text: 'She\'s good at solving problems.' },
        { label: 'instead of', text: 'Instead of complaining, take action.' },
        { label: 'by', text: 'You can improve by practicing regularly.' },
      ],
      warning: 'Common error: "interested in to learn" ❌ → "interested in learning" ✓',
    },
  ],
  exercises: [
    {
      title: 'Choose Gerund or Infinitive',
      type: 'select',
      instructions: 'Select the correct form.',
      questions: [
        {
          text: 'I enjoy ___ in the morning.',
          options: ['running', 'to run', 'run'],
          answer: 'running',
          explanation: '"Enjoy" is always followed by a gerund.',
        },
        {
          text: 'She suggested ___ to the beach.',
          options: ['going', 'to go', 'go'],
          answer: 'going',
          explanation: '"Suggest" takes a gerund.',
        },
        {
          text: 'He\'s interested in ___ a new language.',
          options: ['learning', 'to learn', 'learn'],
          answer: 'learning',
          explanation: 'After prepositions (in), use a gerund.',
        },
        {
          text: 'They avoided ___ about the problem.',
          options: ['talking', 'to talk', 'talk'],
          answer: 'talking',
          explanation: '"Avoid" is always followed by a gerund.',
        },
        {
          text: 'Would you mind ___ the window?',
          options: ['opening', 'to open', 'open'],
          answer: 'opening',
          explanation: '"Mind" is always followed by a gerund.',
        },
        {
          text: 'Keep ___ until you succeed!',
          options: ['trying', 'to try', 'try'],
          answer: 'trying',
          explanation: '"Keep" takes a gerund to express continuation.',
        },
      ],
    },
    {
      title: 'Gerund After Preposition',
      type: 'fill-in',
      instructions: 'Complete the sentence with the gerund form of the verb in parentheses.',
      questions: [
        {
          text: 'I\'m tired of ___ (wait) for the bus.',
          answer: 'waiting',
          explanation: 'After "tired of" (preposition), use a gerund.',
        },
        {
          text: 'She\'s good at ___ (solve) problems.',
          answer: 'solving',
          explanation: 'After "good at" (preposition), use a gerund.',
        },
        {
          text: 'Instead of ___ (complain), take action.',
          answer: 'complaining',
          explanation: 'After "instead of" (preposition), use a gerund.',
        },
        {
          text: 'Thank you for ___ (help) me.',
          answer: 'helping',
          explanation: 'After "for" (preposition), use a gerund.',
        },
      ],
    },
    {
      title: 'Identify the Gerund Function',
      type: 'select',
      instructions: 'What function does the gerund serve in each sentence?',
      questions: [
        {
          text: '"Swimming is great exercise."',
          options: ['Subject', 'Object', 'Complement'],
          answer: 'Subject',
          explanation: '"Swimming" is the subject of the sentence.',
        },
        {
          text: '"I enjoy reading."',
          options: ['Object', 'Subject', 'Complement'],
          answer: 'Object',
          explanation: '"Reading" is the object of "enjoy".',
        },
        {
          text: '"My hobby is painting."',
          options: ['Complement', 'Subject', 'Object'],
          answer: 'Complement',
          explanation: '"Painting" completes the meaning of the subject.',
        },
        {
          text: '"Learning English requires practice."',
          options: ['Subject', 'Object', 'Complement'],
          answer: 'Subject',
          explanation: '"Learning English" is the subject of the sentence.',
        },
      ],
    },
    {
      title: 'Correct the Errors',
      type: 'writing',
      instructions: 'Rewrite each sentence correctly using gerunds.',
      questions: [
        {
          text: '"I\'m interested in to learn more about technology."',
          example: 'I\'m interested in learning more about technology.',
          tip: 'After prepositions, always use a gerund, not an infinitive.',
        },
        {
          text: '"She avoided to talk about her mistake."',
          example: 'She avoided talking about her mistake.',
          tip: '"Avoid" is followed by a gerund.',
        },
        {
          text: '"By to practice every day, you will improve."',
          example: 'By practicing every day, you will improve.',
          tip: 'After "by" (preposition), use a gerund.',
        },
      ],
    },
  ],
  tips: [
    'Memorize verbs that are always followed by gerunds',
    'After prepositions, always use gerunds',
    'Practice identifying gerunds in reading passages',
  ],
  dailyPractice: [
    'Write sentences using gerunds as subjects',
    'Practice verb + gerund combinations',
    'Identify gerunds in IELTS reading texts',
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar Concepts',
  aiContext: 'Gerunds: Verb forms ending in -ing that function as nouns. Used after certain verbs (enjoy, avoid, consider) and always after prepositions.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...gerundsData,
      title: gerundsData.title + ' (Foundations)',
      overview: {
        ...gerundsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: gerundsData.sections.slice(0, Math.max(2, Math.ceil(gerundsData.sections.length / 2))),
      exercises: gerundsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...gerundsData,
      title: gerundsData.title + ' (Advanced)',
      overview: {
        ...gerundsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: gerundsData.sections,
      exercises: gerundsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(gerundsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return gerundsData;
}
